import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { createUseStyles } from '@Sage/hoi-poi-ui';
import { v4 as uuid } from 'uuid';

import { useWeb5Context } from 'lib/Web5Context';
import {
    getCopilotStreamAnswer,
    getCopilotStreamAnswerWithFunctions,
    INTERACTION_TYPES,
} from 'lib/services/copilot';
import copyToClipboard from 'utils/copy';

import { CopilotContext } from '../Context';
import useBuildAnswer from '../hooks/useBuildAnswer';
import useChatStream from '../hooks/useChatStream';
import { COPILOT_BAR_LOADING_STATES } from '../hooks/constants';
import { createToolDonePhrase, createToolInitPhrase } from '../hooks/utils';
import AgentSwitcher from './components/AgentSwitcher';
import Chat from './components/Chat';
import ChatToolbar from './components/ChatToolbar';

import styles from './styles';

const useStyles = createUseStyles(styles, 'CopilotChat');

const CopilotChat = memo(
    ({
        cb, // Callbacks for the Copilot chat events
        chatId,
        chatSuggestions,
        className,
        CustomActionsComponent,
        defaultFirstInteraction,
        enableVoiceInput = false,
        handleEmail,
        handleFeedback,
        handleVote,
        interactions = [],
        loadingPhrases,
        overrides = {},
        session,
    }) => {
        // ========================================
        // CONTEXT & CUSTOM HOOKS
        // ========================================
        const classes = useStyles();
        const { t } = useWeb5Context();
        const { cleanChatTextForClipboard } = useBuildAnswer();

        const {
            chats,
            clearChat,
            functionRegistry,
            getStreamReader,
            getMethodsForCopilot,
            removeInteraction,
            setChatSession,
            setInteraction,
            setIsInitialized,
            setStreamReader,
        } = useContext(CopilotContext);

        // ========================================
        // REFS
        // ========================================
        const contentRef = useRef(null);
        const scrollTimeoutRef = useRef(null);
        const lastAudioDataRef = useRef(null);
        const lastTextMessageRef = useRef(null);

        // ========================================
        // LOCAL STATE
        // ========================================
        const defaultLoadingPhrases = useMemo(() => [t('label_danai_thinking_v3')], [t]);
        const [dynamicPhrases, setDynamicPhrases] = useState(
            loadingPhrases ?? defaultLoadingPhrases,
        );
        const [errorState, setErrorState] = useState(null);

        // ========================================
        // DERIVED STATE & MEMOIZATION
        // ========================================
        const chatState = useMemo(() => chats?.[chatId] ?? {}, [chats, chatId]);
        const { chat, chatSession, isInitialized, reports, tables } = chatState;

        const finalSession = useMemo(() => {
            return {
                ...session,
                inputAudioFormat: enableVoiceInput ? 'pcm16' : undefined,
            };
        }, [session, enableVoiceInput]);

        // ========================================
        // EVENT HANDLERS & CALLBACKS
        // ========================================

        // Scroll Utilities
        const scrollToBottom = useCallback(() => {
            if (contentRef.current) {
                const el = contentRef.current;
                el.scrollTop = el.scrollHeight;
            }
        }, []);

        // Debounced version to prevent multiple rapid scrolls
        const debouncedScrollToBottom = useCallback(
            (delay = 100) => {
                if (scrollTimeoutRef.current) {
                    clearTimeout(scrollTimeoutRef.current);
                }
                scrollTimeoutRef.current = setTimeout(scrollToBottom, delay);
            },
            [scrollToBottom],
        );

        // Callback to handle loading state changes from useChatStream (for error handling)
        const handleLoadingStateChange = useCallback((props) => {
            if (props?.loadingState === COPILOT_BAR_LOADING_STATES.PROCESSING_ERROR) {
                setErrorState(COPILOT_BAR_LOADING_STATES.PROCESSING_ERROR);
            } else if (
                props?.loadingState === COPILOT_BAR_LOADING_STATES.IDLE ||
                props?.loadingState === COPILOT_BAR_LOADING_STATES.LOADING ||
                props?.loadingState === COPILOT_BAR_LOADING_STATES.COMPLETED
            ) {
                setErrorState(null);
            }
        }, []);

        /**
         * Helper to update dynamic phrases based on callback result
         * @param {Object|undefined} result - The result from the callback
         * @param {string[]} result.phrases - Array of phrases to use
         * @param {boolean} [result.merge=false] - Whether to merge with existing phrases
         */
        const updatePhrasesFromResult = useCallback((result) => {
            if (!result?.phrases || !Array.isArray(result.phrases)) return;

            const { phrases, merge = false } = result;

            if (merge) {
                setDynamicPhrases((prev) => {
                    const existingPhrases = Array.isArray(prev) ? prev : [];
                    // Avoid duplicates when merging
                    const newPhrases = phrases.filter((p) => !existingPhrases.includes(p));
                    return [...existingPhrases, ...newPhrases];
                });
            } else {
                setDynamicPhrases(phrases);
            }
        }, []);

        // Wrapper handler for onToolInit that supports dynamic phrases update
        const handleToolInit = useCallback(
            (tool) => {
                // Call custom callback if provided, otherwise use default
                const result = cb?.onToolInit
                    ? cb.onToolInit(tool)
                    : createToolInitPhrase({ t, tool });

                // Update phrases if callback returns a valid result
                updatePhrasesFromResult(result);
            },
            [cb, t, updatePhrasesFromResult],
        );

        // Wrapper handler for onToolDone that supports dynamic phrases update
        const handleToolDone = useCallback(
            (tool) => {
                // Call custom callback if provided, otherwise use default
                const result = cb?.onToolDone
                    ? cb.onToolDone(tool)
                    : createToolDonePhrase({ t, tool });

                // Update phrases if callback returns a valid result
                updatePhrasesFromResult(result);
            },
            [cb, t, updatePhrasesFromResult],
        );

        // ========================================
        // CUSTOM HOOKS
        // ========================================
        const {
            isLoadingAnswer,
            setIsLoadingAnswer,
            refs: { answerInteractionIdRef, questionRef },
            onAnswer,
            onData,
            onError,
            onFinish,
            onAction,
            onArtifact,
            onForm,
            onSetChatSession,
            onTextDone,
            cancelSequence,
            onSubmitChat,
            onSubmitVoice,
            getQuestionRef,
        } = useChatStream({
            chat,
            chatId,
            chatSession,
            debouncedScrollToBottom,
            functionRegistry,
            interactions,
            getStreamReader,
            onActionCallback: cb?.onAction,
            onAnswerCallback: cb?.onAnswer,
            onArtifactCallback: cb?.onArtifact,
            onDataCallback: cb?.onData,
            onErrorCallback: cb?.onError,
            onFinishCallback: cb?.onFinish,
            onFormCallback: cb?.onForm,
            onToolDoneCallback: handleToolDone,
            onToolInitCallback: handleToolInit,
            session: finalSession,
            setChatSession,
            removeInteraction,
            setCopilotBarProps: handleLoadingStateChange,
            setInteraction,
            setStreamReader,
            t,
        });

        const isDanaWritting = useMemo(() => isLoadingAnswer, [isLoadingAnswer]);

        const handleClearChat = useCallback(() => {
            cancelSequence();
            clearChat({ chatId });
            setDynamicPhrases(defaultLoadingPhrases);
            cb?.onClearChat?.();
        }, [cancelSequence, cb, chatId, clearChat, defaultLoadingPhrases]);

        const handleRecordingError = useCallback((error) => {
            console.error('Voice recording error:', error);
            setErrorState(COPILOT_BAR_LOADING_STATES.RECORDING_ERROR);
        }, []);

        const handleDismissError = useCallback(() => {
            setErrorState(null);
        }, []);

        const handleTextSubmit = useCallback(
            (suggestion) => {
                // Store text for potential retry (use suggestion or current input)
                const textToStore = suggestion || questionRef.current || '';
                lastTextMessageRef.current = textToStore;
                // Clear audio data since this is a text submission
                lastAudioDataRef.current = null;
                // Clear any previous error state
                setErrorState(null);
                onSubmitChat(suggestion);
            },
            [onSubmitChat, questionRef],
        );

        const handleVoiceSubmit = useCallback(
            (audioData) => {
                // Store audio data for potential retry
                lastAudioDataRef.current = audioData;
                // Clear text data since this is a voice submission
                lastTextMessageRef.current = null;
                // Clear any previous error state
                setErrorState(null);
                onSubmitVoice(audioData);
            },
            [onSubmitVoice],
        );

        const onCopy = useCallback(
            (value) => {
                if (!value.content) return;

                const text = cleanChatTextForClipboard({
                    value: value.content,
                    tables,
                    reports,
                });
                copyToClipboard(text, t);
            },
            [cleanChatTextForClipboard, reports, t, tables],
        );

        // Utility Functions
        const getContentRef = (ref) => {
            if (ref) {
                contentRef.current = ref;
            }
        };

        // ========================================
        // EFFECTS
        // ========================================
        useEffect(() => {
            if (!isInitialized) {
                setIsInitialized({ chatId, value: true });

                if (defaultFirstInteraction?.text) {
                    setInteraction({
                        chatId,
                        id: uuid(),
                        isFinished: true,
                        isQuestion: false,
                        isError: false,
                        isLoading: false,
                        disableVote: true,
                        ...defaultFirstInteraction,
                    });
                    // Scroll to bottom after setting default interaction
                    debouncedScrollToBottom();
                }

                if (interactions.length > 0 && !answerInteractionIdRef.current) {
                    setIsLoadingAnswer(true);
                    const questionId = uuid();
                    answerInteractionIdRef.current = uuid();

                    // Handle GoldenMinute click on CTA prompts
                    interactions
                        .filter(({ item }) => item.type === INTERACTION_TYPES.MESSAGE)
                        .forEach(({ item }) => {
                            setInteraction({
                                chatId,
                                id: uuid(),
                                text: item.content.text,
                                isFinished: true,
                                isQuestion: true,
                                isError: false,
                                isLoading: false,
                                parentId: questionId,
                            });
                        });

                    setInteraction({
                        chatId,
                        id: answerInteractionIdRef.current,
                        text: '',
                        isLoading: true,
                        parentId: questionId,
                    });

                    getCopilotStreamAnswer({
                        interactions,
                        onAction,
                        onArtifact,
                        onError: onError(questionId),
                        onFinish,
                        onForm,
                        onSetReader: (reader) => setStreamReader({ chatId, reader }),
                        onTextDone,
                        onToolDone: handleToolDone,
                        onToolInit: handleToolInit,
                        session: finalSession,
                        setData: onData,
                        setSession: onSetChatSession,
                        setText: onAnswer,
                    }).finally(() => {
                        setIsLoadingAnswer(false);
                        // Scroll to bottom after initial content loads
                        debouncedScrollToBottom();
                    });
                }
            }
        }, [
            answerInteractionIdRef,
            chatId,
            debouncedScrollToBottom,
            defaultFirstInteraction,
            finalSession,
            handleToolDone,
            handleToolInit,
            interactions,
            isInitialized,
            onAction,
            onAnswer,
            onArtifact,
            onData,
            onError,
            onFinish,
            onForm,
            onSetChatSession,
            onTextDone,
            session,
            setIsInitialized,
            setIsLoadingAnswer,
            setInteraction,
            setStreamReader,
        ]);

        useEffect(() => {
            getMethodsForCopilot.registerFeatureMethods(`chat-${chatId}`, {
                onSubmit: onSubmitChat,
            });
        }, [getMethodsForCopilot, onSubmitChat, chatId]);

        useEffect(() => {
            debouncedScrollToBottom();
            return () => {
                if (scrollTimeoutRef.current) {
                    clearTimeout(scrollTimeoutRef.current);
                }
            };
        }, [debouncedScrollToBottom]);

        // Sync dynamicPhrases when loadingPhrases prop changes
        useEffect(() => {
            setDynamicPhrases(loadingPhrases ?? defaultLoadingPhrases);
        }, [defaultLoadingPhrases, loadingPhrases]);

        // ========================================
        // MEMOIZED COMPONENT PROPS
        // ========================================
        const chatProps = useMemo(
            () => ({
                chatId,
                conversationId: chatSession?.id,
                conversation: chat,
                chatSuggestions,
                isDanaWritting,
                loadingPhrases: dynamicPhrases,
                onCopy,
                onEmail: handleEmail,
                onFeedback: handleFeedback,
                onVote: handleVote,
            }),
            [
                chatId,
                chatSession?.id,
                chat,
                chatSuggestions,
                dynamicPhrases,
                isDanaWritting,
                onCopy,
                handleEmail,
                handleFeedback,
                handleVote,
            ],
        );

        const chatToolbarProps = useMemo(
            () => ({
                conversation: chat,
                enableVoiceInput,
                errorState,
                getQuestionRef,
                isDanaWritting,
                isLoadingAnswer,
                lastAudioData: lastAudioDataRef.current,
                lastTextMessage: lastTextMessageRef.current,
                onClear: handleClearChat,
                onDismissError: handleDismissError,
                onRecordingError: handleRecordingError,
                onSubmit: handleTextSubmit,
                onVoiceSubmit: handleVoiceSubmit,
            }),
            [
                chat,
                enableVoiceInput,
                errorState,
                getQuestionRef,
                handleClearChat,
                handleDismissError,
                handleRecordingError,
                handleTextSubmit,
                handleVoiceSubmit,
                isDanaWritting,
                isLoadingAnswer,
            ],
        );

        // ========================================
        // RENDER
        // ========================================
        return (
            <div className={classNames(classes.root, className)}>
                {chat && (
                    <div className={classes.chat} ref={(ref) => getContentRef(ref)}>
                        <Chat {...chatProps} />
                    </div>
                )}
                {!overrides?.disableChatInput && (
                    <div className={classes.chatBar}>
                        <div className={classes.chatToolbar}>
                            {!overrides?.disableAgentSwitcher && <AgentSwitcher chatId={chatId} />}
                            {!!CustomActionsComponent && (
                                <CustomActionsComponent menuPlacement="topLeft" />
                            )}
                        </div>
                        <div className={classes.input}>
                            <ChatToolbar {...chatToolbarProps} />
                        </div>
                    </div>
                )}
            </div>
        );
    },
);

export default CopilotChat;

export { getCopilotStreamAnswer, getCopilotStreamAnswerWithFunctions, ChatToolbar };
